import React, { useMemo, useState } from 'react';
import { MoodboardItem, ItemType } from './types/moodboard';
import { computeAiInsights } from './utils/ai-insights';
import { MoodboardGrid } from './components/MoodboardGrid';
import { AddItemDialog } from './components/AddItemDialog';
import { AiInsightsPanel } from './components/AiInsightsPanel';
import { GroupingView } from './components/GroupingView';
import { Lightbox } from './components/Lightbox';
import { TypeFilter } from './components/TypeFilter';
import { Lightbulb, X, Layers, LayoutGrid, Search, Plus } from 'lucide-react';
const INITIAL_ITEMS: MoodboardItem[] = [{
  id: '1',
  type: 'image',
  title: 'LAYERING SARTORIALE',
  content: 'https://cdn.magicpatterns.com/uploads/uEbvA5zpJWtJ614bMwYUxQ/intro_cover_articolo_blog_abbinamenti_colori_uomo_2025_nobile_1982.jpg',
  description: 'Cappotto cammello su gilet in tweed verde, con Principe di Galles grigio. Riferimento layering per la FW25.',
  tags: ['SARTORIA', 'LAYERING', 'UOMO', 'FW25'],
  createdAt: Date.now() - 100000
}, {
  id: '2',
  type: 'note',
  content: "Dove la tradizione incontra l'audacia. Linee pulite, palette monocromatiche, proporzioni inattese.",
  tags: ['DIREZIONE', 'MINIMALISMO', 'AUDACIA'],
  createdAt: Date.now() - 90000
}, {
  id: '3',
  type: 'color_palette',
  content: '#FFFFFF, #E5E5E5, #999999, #666666, #1A1A1A, #000000',
  tags: ['PALETTE BASE', 'MONOCROMO'],
  createdAt: Date.now() - 80000
}, {
  id: '4',
  type: 'image',
  title: 'TEXTURE — POIS',
  content: 'https://cdn.magicpatterns.com/uploads/122ySnFgeiTHtAsVb6vBN5/images.jpg',
  description: 'Tessuto a pois in tonalità calde, rosa e arancio. Spunto per accenti tessili.',
  tags: ['TEXTURE', 'TESSUTO', 'FANTASIA'],
  createdAt: Date.now() - 70000
}, {
  id: '5',
  type: 'link',
  title: 'VELASCA — MARZAMEMI CHINO',
  content: 'https://it.velasca.com/products/marzamemi-blgcsl',
  description: 'Chino con pince in gabardina di cotone stretch e lyocell — €180. Primavera/Estate.',
  tags: ['RIFERIMENTO', 'BRAND', 'VELASCA', 'CHINO'],
  createdAt: Date.now() - 60000
}, {
  id: '6',
  type: 'image',
  title: 'DETTAGLIO — LANA A QUADRI',
  content: 'https://cdn.magicpatterns.com/uploads/uA1m81DFaQguGdtsmV24FY/texhomme-030119-088.webp',
  description: 'Lana a quadri blu e grigio con riga marrone. Ricerca materiali per giacche e abiti.',
  tags: ['TEXTURE', 'LANA', 'ABITI'],
  createdAt: Date.now() - 50000
}, {
  id: '7',
  type: 'color_palette',
  content: '#F5F0EB, #D4C5B5, #8C7A6B, #4A4036',
  tags: ['TONI TERRA', 'PALETTE ACCENTO'],
  createdAt: Date.now() - 45000
}, {
  id: '8',
  type: 'image',
  title: 'PITTI UOMO — STREET STYLE',
  content: 'https://cdn.magicpatterns.com/uploads/2C1tNsehGhjWtX6aJFmAh7/eleganza-a-pitti-86.jpg',
  description: 'Tre gentiluomini a Pitti 86. Abiti estivi in lino e cotone. Ispirazione stile.',
  tags: ['PITTI', 'STREET STYLE', 'ESTATE'],
  createdAt: Date.now() - 40000
}, {
  id: '9',
  type: 'note',
  content: 'Le finiture metalliche devono essere invisibili o nero opaco. Zero lucentezza. Tutto il focus sul taglio e sulla caduta del tessuto.',
  tags: ['FINITURE', 'DETTAGLIO', 'OPACO'],
  createdAt: Date.now() - 30000
}, {
  id: '10',
  type: 'image',
  title: 'TRE PEZZI IN TWEED',
  content: 'https://cdn.magicpatterns.com/uploads/bqzSu2RywQukQBUXsSYcts/p1080388.jpg',
  description: 'Completo tre pezzi in tweed grigio con coppola. Sartoria britannica che incontra lo street italiano.',
  tags: ['TWEED', 'TRE PEZZI', 'UOMO'],
  createdAt: Date.now() - 20000
}, {
  id: '11',
  type: 'note',
  content: 'Silhouette FW25: spalle naturali, vita leggermente segnata, pantaloni ampi con doppia pince. Il volume è nel basso — giacche corte su pantaloni wide. Nessun padding.',
  tags: ['COSTRUZIONE', 'SILHOUETTE', 'VOLUME', 'FW25'],
  createdAt: Date.now() - 18000
}, {
  id: '12',
  type: 'note',
  content: 'Il mood è un gentleman che ha vissuto. Non perfetto, non nuovo. Tessuti che sembrano già indossati, colori che ricordano la terra dopo la pioggia. Eleganza stanca, mai forzata.',
  tags: ['MOOD', 'VISIONE', 'ESTETICA', 'CONCETTO'],
  createdAt: Date.now() - 16000
}, {
  id: '13',
  type: 'link',
  title: 'BRUNELLO CUCINELLI — FW25 LOOKBOOK',
  content: 'https://www.brunellocucinelli.com/it/uomo/look/',
  description: 'Benchmark principale: layering rilassato, palette neutre, cashmere e lino. Riferimento per posizionamento prezzo e qualità percepita.',
  tags: ['BENCHMARK', 'COMPETITOR', 'BRAND', 'CASHMERE'],
  createdAt: Date.now() - 14000
}, {
  id: '14',
  type: 'note',
  content: 'Bottoni in corozo tinto in capo, asole a mano su giacche e gilet. Impunture a contrasto solo su tasche interne. Zip invisibili YKK Excella in finitura brunita.',
  tags: ['DETTAGLIO', 'BOTTONI', 'FINITURE', 'IMPUNTURE'],
  createdAt: Date.now() - 12000
}, {
  id: '15',
  type: 'color_palette',
  content: '#2C3E50, #8E6F47, #C19A6B, #E8D5B7, #F2EDE4',
  tags: ['PALETTE', 'TONI TERRA', 'CROMIA'],
  createdAt: Date.now() - 10000
}, {
  id: '16',
  type: 'note',
  content: 'Fornitori tessuti: Vitale Barberis Canonico per lane, Albini per camiceria, Limonta per tecnici. Campionatura entro marzo, ordini definitivi entro maggio.',
  tags: ['MATERIA', 'TESSUTO', 'LANA', 'COTONE'],
  createdAt: Date.now() - 8000
}];
// Map hex colors to descriptive keywords for matching against image descriptions/tags
function hexToColorKeywords(hex: string): string[] {
  const h = hex.toLowerCase();
  const colorMap: Record<string, string[]> = {
    // Blacks
    '#000000': ['black', 'dark', 'nero', 'opaco', 'scuro'],
    '#1a1a1a': ['black', 'dark', 'nero', 'opaco', 'scuro', 'charcoal'],
    // Whites
    '#ffffff': ['white', 'bianco', 'light', 'cream', 'chiaro'],
    '#f5f0eb': ['cream', 'ivory', 'beige', 'ecru', 'cammello', 'camel', 'panna'],
    // Greys
    '#e5e5e5': ['grey', 'gray', 'silver', 'grigio', 'chiaro'],
    '#999999': ['grey', 'gray', 'grigio', 'principe di galles', 'quadri'],
    '#666666': ['grey', 'gray', 'grigio', 'scuro', 'charcoal', 'tweed'],
    // Earth tones
    '#d4c5b5': ['beige', 'sabbia', 'cammello', 'camel', 'lino', 'cotone', 'khaki'],
    '#8c7a6b': ['marrone', 'brown', 'terra', 'cammello', 'tweed', 'lana'],
    '#4a4036': ['marrone', 'brown', 'cioccolato', 'terra', 'tweed', 'scuro']
  };
  // Direct match
  if (colorMap[h]) return colorMap[h];
  // Fallback: parse RGB and classify by hue/lightness
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  const lightness = (r + g + b) / 3;
  const keywords: string[] = [];
  if (lightness > 220) keywords.push('bianco', 'chiaro', 'panna', 'white');else if (lightness > 170) keywords.push('chiaro', 'beige', 'grigio', 'grey');else if (lightness > 100) keywords.push('grigio', 'grey', 'medio');else if (lightness > 50) keywords.push('scuro', 'grigio', 'charcoal');else keywords.push('nero', 'scuro', 'black');
  // Detect warm vs cool
  if (r > b + 30) keywords.push('caldo', 'marrone', 'cammello', 'terra');
  if (b > r + 30) keywords.push('blu', 'freddo', 'navy');
  if (g > r + 20 && g > b + 20) keywords.push('verde', 'oliva');
  if (r > 150 && g < 100 && b < 100) keywords.push('rosso', 'borgogna');
  return keywords;
}
export function App() {
  const [items, setItems] = useState<MoodboardItem[]>(INITIAL_ITEMS);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'grouping'>('grid');
  const [highlightedItemIds, setHighlightedItemIds] = useState<Set<string> | null>(null);
  const [activeHighlight, setActiveHighlight] = useState<{
    type: string;
    value: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState<ItemType | 'all'>('all');
  const [lightboxItemId, setLightboxItemId] = useState<string | null>(null);
  const handleAddItem = (newItemData: Omit<MoodboardItem, 'id' | 'createdAt'>) => {
    const newItem: MoodboardItem = {
      ...newItemData,
      id: Math.random().toString(36).substring(2, 9),
      createdAt: Date.now()
    };
    setItems(prev => [newItem, ...prev]);
  };
  const handleDeleteItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };
  const handleHighlight = (type: 'color' | 'tag', value: string | null) => {
    if (!value) {
      setHighlightedItemIds(null);
      setActiveHighlight(null);
      return;
    }
    // Toggle off if clicking the same thing
    if (activeHighlight?.type === type && activeHighlight?.value === value) {
      setHighlightedItemIds(null);
      setActiveHighlight(null);
      return;
    }
    setActiveHighlight({
      type,
      value
    });
    const ids = new Set<string>();
    items.forEach(item => {
      if (type === 'color') {
        // Match color_palette items containing this color
        if (item.type === 'color_palette' && item.content.includes(value)) {
          ids.add(item.id);
        }
        // Also match images/notes/links by color keywords in description and tags
        const colorKeywords = hexToColorKeywords(value);
        const searchText = [item.description || '', item.title || '', ...item.tags].join(' ').toLowerCase();
        if (colorKeywords.some(kw => searchText.includes(kw))) {
          ids.add(item.id);
        }
      } else if (type === 'tag') {
        // Case insensitive match for tags
        if (item.tags.some(t => t.toLowerCase() === value.toLowerCase() || t.toLowerCase().includes(value.toLowerCase()) || value.toLowerCase().includes(t.toLowerCase()))) {
          ids.add(item.id);
        }
      }
    });
    setHighlightedItemIds(ids);
  };
  // Clear highlights when closing insights
  const handleToggleInsights = () => {
    if (insightsOpen) {
      setHighlightedItemIds(null);
      setActiveHighlight(null);
    }
    setInsightsOpen(!insightsOpen);
  };
  const insights = useMemo(() => computeAiInsights(items), [items]);
  // Filter items by search and type
  const filteredItems = useMemo(() => {
    let result = items;
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => {
        const searchable = [item.title || '', item.content || '', item.description || '', ...item.tags].join(' ').toLowerCase();
        return searchable.includes(q);
      });
    }
    // Type filter
    if (activeTypeFilter !== 'all') {
      result = result.filter(item => item.type === activeTypeFilter);
    }
    return result;
  }, [items, searchQuery, activeTypeFilter]);
  // Compute type counts
  const typeCounts = useMemo(() => {
    const counts: Record<ItemType | 'all', number> = {
      all: items.length,
      image: 0,
      note: 0,
      color_palette: 0,
      link: 0
    };
    items.forEach(item => {
      counts[item.type]++;
    });
    return counts;
  }, [items]);
  // Lightbox handlers
  const imageItems = useMemo(() => items.filter(item => item.type === 'image'), [items]);
  const lightboxItem = useMemo(() => items.find(item => item.id === lightboxItemId) || null, [items, lightboxItemId]);
  const currentImageIndex = useMemo(() => {
    if (!lightboxItemId) return -1;
    return imageItems.findIndex(item => item.id === lightboxItemId);
  }, [imageItems, lightboxItemId]);
  const handleLightboxPrevious = () => {
    if (currentImageIndex > 0) {
      setLightboxItemId(imageItems[currentImageIndex - 1].id);
    }
  };
  const handleLightboxNext = () => {
    if (currentImageIndex < imageItems.length - 1) {
      setLightboxItemId(imageItems[currentImageIndex + 1].id);
    }
  };
  return <div className="min-h-screen bg-white text-black font-sans flex flex-col" data-id="element-0">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full bg-white border-b border-border" data-id="element-1">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center gap-3" data-id="element-3">
            <h1 className="font-heading font-bold text-[16px] sm:text-[18px] tracking-[0.35em] uppercase" data-id="element-4">
              GRUPPO NAMAN
            </h1>
            <span className="hidden sm:inline text-[#999] font-heading text-[11px] tracking-[0.2em] uppercase" data-id="element-5">
              · {filteredItems.length} ELEMENTI
            </span>
          </div>

          {/* Search Bar */}
          <div className="hidden sm:flex items-center flex-1 max-w-sm mx-8" data-id="element-6">
            <div className="relative w-full" data-id="element-7">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" strokeWidth={1.5} data-id="element-8" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca nella board..." className="w-full h-9 pl-9 pr-3 bg-[#f5f5f5] border border-transparent focus:border-border focus:bg-white text-[12px] font-sans tracking-wide outline-none transition-colors placeholder:text-[#999] rounded-full" data-id="element-9" />
            </div>
          </div>

          <nav className="flex items-center gap-4 sm:gap-5" data-id="element-10">
            {/* Mobile Search Toggle */}
            <button className="sm:hidden text-[#999] hover:text-black transition-colors" onClick={() => {
            const el = document.getElementById('mobile-search');
            if (el) el.classList.toggle('hidden');
          }} aria-label="Cerca" data-id="element-11">
              <Search className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-12" />
            </button>

            <button className={`transition-colors ${viewMode === 'grouping' ? 'text-black' : 'text-[#999] hover:text-black'}`} onClick={() => setViewMode(viewMode === 'grid' ? 'grouping' : 'grid')} aria-label={viewMode === 'grid' ? 'Vista Raggruppamento' : 'Vista Griglia'} data-id="element-13">
              {viewMode === 'grid' ? <Layers className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-14" /> : <LayoutGrid className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-15" />}
            </button>

            <AddItemDialog onAdd={handleAddItem} existingTags={items.flatMap(i => i.tags)} data-id="element-16" />

            <div className="w-px h-5 bg-border" data-id="element-17" />

            <button className={`transition-colors ${insightsOpen ? 'text-black' : 'text-[#999] hover:text-black'}`} onClick={handleToggleInsights} aria-label={insightsOpen ? 'Chiudi Insights' : 'Apri Insights'} data-id="element-18">
              {insightsOpen ? <X className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-19" /> : <Lightbulb className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-20" />}
            </button>
          </nav>
        </div>

        {/* Mobile Search Bar */}
        <div id="mobile-search" className="hidden sm:hidden px-4 pb-3" data-id="element-21">
          <div className="relative w-full" data-id="element-22">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" strokeWidth={1.5} data-id="element-23" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca nella board..." className="w-full h-9 pl-9 pr-3 bg-[#f5f5f5] border border-transparent focus:border-border focus:bg-white text-[12px] font-sans tracking-wide outline-none transition-colors placeholder:text-[#999] rounded-full" data-id="element-24" />
          </div>
        </div>
      </header>

      {/* Type Filter */}
      {viewMode === 'grid' && <TypeFilter activeFilter={activeTypeFilter} onFilterChange={setActiveTypeFilter} counts={typeCounts} data-id="element-25" />}

      {/* Main Content Area */}
      <div className="flex flex-1 max-w-[1600px] mx-auto w-full relative" data-id="element-26">
        {/* Main View (Grid or Grouping) */}
        <main className="w-full transition-all duration-500 ease-in-out" data-id="element-27">
          {viewMode === 'grid' ? <div className="px-4 sm:px-8 py-6 sm:py-12" data-id="element-28">
              <MoodboardGrid items={filteredItems} onDelete={handleDeleteItem} highlightedItemIds={highlightedItemIds} onImageClick={setLightboxItemId} data-id="element-29" />
            </div> : <div className="w-full h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)]" data-id="element-30">
              <GroupingView items={filteredItems} data-id="element-31" />
            </div>}
        </main>
      </div>

      {/* AI Insights Panel (Now Full Page Overlay) */}
      <AiInsightsPanel open={insightsOpen} onOpenChange={handleToggleInsights} insights={insights} onHighlight={handleHighlight} activeHighlight={activeHighlight} data-id="element-32" />

      {/* Lightbox */}
      {lightboxItemId && <Lightbox item={lightboxItem} onClose={() => setLightboxItemId(null)} onPrevious={handleLightboxPrevious} onNext={handleLightboxNext} hasPrevious={currentImageIndex > 0} hasNext={currentImageIndex < imageItems.length - 1} data-id="element-33" />}

      {/* Footer */}
      {viewMode === 'grid' && <footer className="bg-black text-white py-10 sm:py-16 mt-auto" data-id="element-34">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-8 text-center" data-id="element-35">
            <p className="font-heading font-light text-[10px] sm:text-[12px] tracking-[0.3em] uppercase text-white/60" data-id="element-36">
              MOODBOARD — DIREZIONE CREATIVA
            </p>
          </div>
        </footer>}
    </div>;
}